"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { CategoryItem } from "@/types";
import { createCategoryKeyword } from "./categoryKeywordsApi";
import { CategoryKeywordsSection } from "./CategoryKeywordsSection";
import {
  CATEGORY_COLOR_SWATCHES,
  DEFAULT_CATEGORY_COLOR,
} from "./categoryColors";

type Props = {
  open: boolean;
  onClose: () => void;
  presetType?: "income" | "expense";
  parentId?: string | null;
  categories: CategoryItem[];
  onCreate: (
    name: string,
    color: string,
    type: "income" | "expense",
    parentCategoryId?: string | null,
  ) => Promise<CategoryItem>;
};

export function AddCategoryModal({
  open,
  onClose,
  presetType,
  parentId,
  categories,
  onCreate,
}: Props) {
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(DEFAULT_CATEGORY_COLOR);
  const [kind, setKind] = useState<"income" | "expense">(
    presetType ?? "expense",
  );
  const [selectedParentId, setSelectedParentId] = useState<string | null>(
    parentId ?? null,
  );
  const [draftKeywords, setDraftKeywords] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setColor(DEFAULT_CATEGORY_COLOR);
    setKind(presetType ?? "expense");
    setSelectedParentId(parentId ?? null);
    setDraftKeywords([]);
    setError(null);
  }, [open, parentId, presetType]);

  const parentOptions = useMemo(() => {
    return categories
      .filter(
        (category) =>
          category.type === kind && category.parentCategoryId === null,
      )
      .map((category) => ({ value: category.id, label: category.name }));
  }, [categories, kind]);

  useEffect(() => {
    if (parentId !== undefined) {
      setSelectedParentId(parentId);
      return;
    }
    if (!selectedParentId) return;
    const parentExists = parentOptions.some((option) => option.value === selectedParentId);
    if (!parentExists) {
      setSelectedParentId(null);
    }
  }, [parentId, parentOptions, selectedParentId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await onCreate(name.trim(), color, kind, selectedParentId);
      const trimmed = draftKeywords.map((k) => k.trim()).filter(Boolean);
      const results = await Promise.allSettled(
        trimmed.map((kw) => createCategoryKeyword(created.id, kw)),
      );
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length > 0) {
        window.alert(
          "Category was created, but one or more keywords could not be added. Edit this category to add keywords manually.",
        );
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not create category",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      opened={open}
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      padding="xl"
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
      title={
        <Group gap="xs">
          <IconPlus size={20} stroke={1.5} />
          <Text size="lg" fw={600}>
            Add category
          </Text>
        </Group>
      }
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
        header: {
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: 16,
          marginBottom: 8,
        },
      }}
    >
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="lg">
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
            radius="md"
            disabled={saving}
            autoFocus
            styles={{
              label: { fontWeight: 500, marginBottom: 8 },
            }}
          />

          {!presetType ? (
            <Select
              label="Type"
              data={[
                { value: "income", label: "Income" },
                { value: "expense", label: "Expense" },
              ]}
              value={kind}
              onChange={(v) => setKind(v === "income" ? "income" : "expense")}
              disabled={saving}
              radius="md"
              styles={{
                label: { fontWeight: 500, marginBottom: 8 },
              }}
            />
          ) : null}

          {parentId === undefined ? (
            <Select
              label="Parent category"
              placeholder="Top level"
              clearable
              data={parentOptions}
              value={selectedParentId}
              onChange={setSelectedParentId}
              disabled={saving}
              radius="md"
              styles={{
                label: { fontWeight: 500, marginBottom: 8 },
              }}
            />
          ) : null}

          <div>
            <Text size="sm" fw={500} mb={8}>
              Color
            </Text>
            <div className="flex flex-wrap gap-3">
              {CATEGORY_COLOR_SWATCHES.map((swatch) => {
                const isSelected = color.toLowerCase() === swatch.toLowerCase();
                return (
                  <button
                    key={swatch}
                    type="button"
                    onClick={() => setColor(swatch)}
                    disabled={saving}
                    aria-label={`Select ${swatch} color`}
                    className={`h-10 w-10 rounded-full border-2 transition ${
                      isSelected
                        ? "border-slate-900 ring-2 ring-slate-900/20"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    style={{ backgroundColor: swatch }}
                  />
                );
              })}
            </div>
          </div>

          <CategoryKeywordsSection
            variant="draft"
            keywords={draftKeywords}
            onChange={setDraftKeywords}
            disabled={saving}
          />

          {error ? (
            <Text size="sm" c="red">
              {error}
            </Text>
          ) : null}

          <Group justify="flex-end" mt="md">
            <Button
              type="button"
              variant="default"
              onClick={onClose}
              disabled={saving}
              radius="md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!name.trim()}
              radius="md"
              styles={{
                root: {
                  backgroundColor: "#030213",
                  "&:hover": { backgroundColor: "#1a1a2e" },
                },
              }}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
